import { useState } from "react";
import { FaCopy, FaEnvelope, FaEye, FaEyeSlash } from "react-icons/fa";
import { sendRequest } from "../../utils/api";
import { useToast } from "../../context/ToastContext";
import FullPageSpinner from "../../components/FullPageSpinner";

interface Props {
  email: string;
  username?: string;
  tempPassword: string;
  isSelfRegister?: boolean;
  onClose?: () => void;
  onRedirectToLogin?: () => void;
}

export default function NewUserModal({
  email,
  username,
  tempPassword,
  isSelfRegister = false,
  onClose,
  onRedirectToLogin,
}: Props) {

  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);

  const [copied, setCopied] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const copyToClipboard = async (text: string, label: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setCopied(`${label} copied!`);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        setCopied(`${label} copied!`);
      }
      setTimeout(() => setCopied(""), 2000);
    } catch {
      setCopied("Copy failed");
    }
  };

  const shareViaEmail = async () => {
    // const subject = encodeURIComponent("Your Wiventory Staff Account");
    // const body = encodeURIComponent(
    //   `Hello,\n\nYour staff account has been created in Wiventory.\n\nLogin Details:\n${username ? `- Username: ${username}\n` : ""
    //   }- Email: ${email}\n- Temporary Password: ${tempPassword}\n\nPlease log in and reset your password immediately.\n\nBest regards,\nWiventory Admin`
    // );
    // window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;

    try {
      setLoading(true);
      await sendRequest(
        "POST", "/users/send-mail",
        { email, type: "loginDetails", data: { tempPassword } }
      );
      onClose?.();
      showToast("Staff login details sent successfully!", {
        type: "success",
      });
    } catch (err: any) {
      showToast(
        "Failed to send invite. Try again.",
        { type: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">

      <FullPageSpinner show={loading} />

      <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-md">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">
          {isSelfRegister ? "Account Created Successfully!" : "Staff Account Created"}
        </h2>

        <p className="text-sm text-red-600 mb-5 text-center">
          {isSelfRegister
            ? "Your account has been created successfully. Please use the login details below."
            : "This password will only be shown once. Please copy it securely or share as needed."}
        </p>

        <div className="space-y-3">
          {username && (
            <div className="flex justify-between items-center border p-3 rounded-md bg-gray-50">
              <span className="font-medium text-gray-700">Username</span>
              <span className="text-gray-800">{username}</span>
              <button
                onClick={() => copyToClipboard(username, "Username")}
                className="ml-2 text-blue-600 hover:text-blue-800"
              >
                {FaCopy({})}
              </button>
            </div>
          )}

          <div className="flex justify-between items-center border p-3 rounded-md bg-gray-50">
            <span className="font-medium text-gray-700">Email</span>
            <span className="text-gray-800">{email}</span>
            <button
              onClick={() => copyToClipboard(email, "Email")}
              className="ml-2 text-blue-600 hover:text-blue-800"
            >
              {FaCopy({})}
            </button>
          </div>

          <div className="flex justify-between items-center border p-3 rounded-md bg-gray-50">
            <span className="font-medium text-gray-700">Password</span>
            <span className="text-gray-800">
              {showPassword ? tempPassword : "••••••••"}
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-600 hover:text-gray-800"
              >
                {showPassword ? <span>{FaEyeSlash({})}</span> : <span>{FaEye({})}</span>}
              </button>
              <button
                onClick={() => copyToClipboard(tempPassword, "Password")}
                className="text-blue-600 hover:text-blue-800"
              >
                {FaCopy({})}
              </button>
            </div>
          </div>
        </div>

        {copied && (
          <p className="text-xs text-green-600 mt-3 text-right">{copied}</p>
        )}

        <div className="flex justify-end mt-6 space-x-3">
          {isSelfRegister ? (
            <button
              onClick={onRedirectToLogin}
              className="w-full px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Go to Login
            </button>
          ) : (
            <>
              <button onClick={shareViaEmail}
                className="flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700" >
                <span className="mr-2" >{FaEnvelope({})}</span> Share via Email
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-gray-500 text-white hover:bg-gray-600"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}